import { createTheme, alpha } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import './theme.d.ts';

// ── FONT CONSTANTS ─────────────────────────────────────
const FONT_DISPLAY = '"Cormorant Garamond", serif';
// Used for: headings, album names, event titles,
//           large stat numbers, hero text, modal titles

const FONT_BODY    = '"DM Sans", sans-serif';
// Used for: all UI text, buttons, nav, body copy,
//           form fields, descriptions, menu items

const FONT_MONO    = '"DM Mono", monospace';
// Used for: labels, chips, badges, timestamps,
//           table headers, stat sublabels,
//           status tags (Public/Private/Analytics)

// ── DESIGN TOKENS ──────────────────────────────────────
const tokens = {
  // Backgrounds
  bgBase:      '#080A0F',
  bgSurface:   '#0E1118',
  bgElevated:  '#141820',
  bgOverlay:   '#1A1F2A',

  // Brand
  teal:        '#00C4A7',
  tealDim:     'rgba(0, 196, 167, 0.10)',
  tealGlow:    'rgba(0, 196, 167, 0.20)',

  // Semantic accents
  amber:       '#F0A500',
  amberDim:    'rgba(240, 165, 0, 0.10)',
  blue:        '#5B8AF5',
  blueDim:     'rgba(91, 138, 245, 0.10)',
  green:       '#22C55E',
  greenDim:    'rgba(34, 197, 94, 0.10)',
  red:         '#FF4D6A',
  redDim:      'rgba(255, 77, 106, 0.10)',

  // Text
  textPrimary:   '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.60)',
  textMuted:     'rgba(255, 255, 255, 0.35)',
  textDisabled:  'rgba(255, 255, 255, 0.18)',

  // Borders
  border:        'rgba(255, 255, 255, 0.07)',
  borderSubtle:  'rgba(255, 255, 255, 0.04)',
  borderStrong:  'rgba(255, 255, 255, 0.14)',
};

// ── THEME ───────────────────────────────────────────────
const theme = createTheme({

  // ── PALETTE ──────────────────────────────────────────
  palette: {
    mode: 'dark',
    background: {
      default: tokens.bgBase,
      paper:   tokens.bgSurface,
    },
    primary: {
      main:         tokens.teal,
      light:        '#00DFC0',
      dark:         '#009E87',
      contrastText: '#000000',
    },
    secondary: {
      main:         tokens.amber,
      light:        '#F5BE40',
      dark:         '#C08200',
      contrastText: '#000000',
    },
    error: {
      main:         tokens.red,
      light:        '#FF7A8E',
      dark:         '#CC2044',
      contrastText: '#FFFFFF',
    },
    warning: {
      main:         tokens.amber,
      contrastText: '#000000',
    },
    success: {
      main:         tokens.green,
      contrastText: '#000000',
    },
    info: {
      main:         tokens.blue,
      contrastText: '#FFFFFF',
    },
    text: {
      primary:   tokens.textPrimary,
      secondary: tokens.textSecondary,
      disabled:  tokens.textDisabled,
    },
    divider: tokens.border,
    action: {
      hover:              'rgba(255, 255, 255, 0.05)',
      selected:           tokens.tealDim,
      disabledBackground: 'rgba(255, 255, 255, 0.04)',
    },
  },

  // ── TYPOGRAPHY ───────────────────────────────────────
  // Default font: DM Sans (body/UI)
  // Headings h1–h4: Cormorant Garamond (display)
  // Labels/chips/mono: DM Mono (applied per component)
  typography: {
    fontFamily: FONT_BODY, // DM Sans — global default

    // ── Cormorant Garamond — display headings ──
    h1: {
      fontFamily: FONT_DISPLAY, // Cormorant Garamond
      fontSize: '3.5rem',
      fontWeight: 300,
      letterSpacing: '-0.03em',
      lineHeight: 1.0,
      // Example use: page hero titles,
      //              event name in hero section
   },
   h2: {
      fontFamily: FONT_DISPLAY, // Cormorant Garamond
      fontSize: '2.25rem',
      fontWeight: 300,
      letterSpacing: '-0.02em',
      lineHeight: 1.1,
      // Example use: section headings,
      //              album name on album page
   },
   h3: {
      fontFamily: FONT_DISPLAY, // Cormorant Garamond
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
      // Example use: card titles, modal headings
   },
   h4: {
      fontFamily: FONT_DISPLAY, // Cormorant Garamond
      fontSize: '1.375rem',
      fontWeight: 600,
      lineHeight: 1.3,
      // Example use: sidebar section titles,
      //              feed page title "Activity"
    },

    // ── DM Sans — UI headings ──
    h5: {
      fontFamily: FONT_BODY, // DM Sans
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
      // Example use: card sub-headers,
      //              participant names
   },
   h6: {
      fontFamily: FONT_BODY, // DM Sans
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.4,
      // Example use: small card titles,
      //              dropdown section headers
   },

    // ── DM Sans — body text ──
   body1: {
      fontFamily: FONT_BODY, // DM Sans
      fontSize: '0.9375rem',
      lineHeight: 1.8,
      color: tokens.textSecondary,
      // Example use: card descriptions,
      //              event descriptions, main copy
   },
   body2: {
      fontFamily: FONT_BODY, // DM Sans
      fontSize: '0.8125rem',
      lineHeight: 1.65,
      color: tokens.textMuted,
      // Example use: secondary card text,
      //              helper text, metadata lines
    },

    // ── DM Mono — labels and metadata ──
    overline: {
      fontFamily: FONT_MONO, // DM Mono
      fontSize: '0.625rem',
      fontWeight: 500,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      lineHeight: 1.5,
      // Example use: "OVERVIEW", "EXPERIENCES",
      //              sidebar section labels,
      //              "NEW ALBUM CREATED" card labels
    },
    caption: {
      fontFamily: FONT_MONO, // DM Mono
      fontSize: '0.6875rem',
      letterSpacing: '0.08em',
      color: tokens.textMuted,
      // Example use: timestamps ("4 hours ago"),
      //              photo counts, viewer counts
    },

    // ── DM Sans — button text ──
   button: {
      fontFamily: FONT_BODY, // DM Sans
      fontWeight: 700,
      letterSpacing: '0.01em',
      textTransform: 'none', // CRITICAL — no uppercase
      // Example use: "Approve Access", "View Album",
      //              "Request RSVP", all CTAs
    },

    subtitle1: {
      fontFamily: FONT_BODY, // DM Sans
      fontSize: '0.9375rem',
      fontWeight: 500,
      // Example use: album owner name,
      //              event host name
    },
    subtitle2: {
      fontFamily: FONT_MONO, // DM Mono
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.1em',
      // Example use: "Hosted by", "By Emmanuel",
      //              small metadata labels
    },
  },

  // ── SHAPE ────────────────────────────────────────────
  shape: {
    borderRadius: 12,
  },

  // ── SHADOWS ──────────────────────────────────────────
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)',
    '0 2px 6px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)',
    '0 4px 12px rgba(0,0,0,0.45), 0 8px 24px rgba(0,0,0,0.3)',
    '0 6px 20px rgba(0,0,0,0.5)',
    '0 8px 28px rgba(0,0,0,0.5)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
    '0 8px 32px rgba(0,0,0,0.55)',
  ] as any,

  // ── COMPONENT OVERRIDES ──────────────────────────────
  components: {

    // GLOBAL BASELINE
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bgBase,
          color: tokens.textPrimary,
          fontFamily: FONT_BODY, // DM Sans
          scrollbarWidth: 'thin',
          scrollbarColor: `${tokens.border} transparent`,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: tokens.border,
            borderRadius: '4px',
          },
        },
      },
    },

    // ── BUTTONS ────────────────────────────────────────
    // Font: DM Sans Bold — via typography.button above
   MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
         root: {
          fontFamily: FONT_BODY, // DM Sans
          fontWeight: 700,
          textTransform: 'none',
          letterSpacing: '0.01em',
          borderRadius: '10px',
          transition: 'all 0.2s ease',
         },
         containedPrimary: {
          background: tokens.teal,
          color: '#000000',
            '&:hover': {
            background: '#00DFC0',
            transform: 'translateY(-1px)',
            boxShadow: '0 8px 24px rgba(0,196,167,0.30)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        outlined: {
          background: 'rgba(255,255,255,0.05)',
          borderColor: tokens.border,
          color: tokens.textSecondary,
          '&:hover': {
            background: 'rgba(255,255,255,0.09)',
            borderColor: tokens.borderStrong,
            color: tokens.textPrimary,
          },
        },
        text: {
          color: tokens.textSecondary,
          '&:hover': {
            background: 'rgba(255,255,255,0.05)',
            color: tokens.textPrimary,
          },
        },
        sizeLarge:  { padding: '14px 32px', fontSize: '0.9375rem' },
        sizeMedium: { padding: '10px 22px', fontSize: '0.875rem' },
        sizeSmall:  {
          padding: '6px 14px',
          fontSize: '0.8125rem',
          borderRadius: '8px'
         },
      },
   },

    MuiIconButton: {
      styleOverrides: {
         root: {
          color: tokens.textMuted,
          borderRadius: '10px',
          transition: 'all 0.15s ease',
          '&:hover': {
            background: 'rgba(255,255,255,0.06)',
            color: tokens.textPrimary,
            },
         },
      },
   },

    // ── CARDS ──────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
         root: {
          background: tokens.bgSurface,
          backgroundImage: 'none', // removes MUI dark
                                   // mode elevation overlay
          border: `1px solid ${tokens.border}`,
          borderRadius: '16px',
          boxShadow: 'none',
          transition: 'border-color 0.2s ease',
          '&:hover': { borderColor: tokens.borderStrong },
         },
      },
   },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '18px 20px',
          '&:last-child': { paddingBottom: '18px' },
         },
      },
   },

    MuiPaper: {
      styleOverrides: {
         root: {
          background: tokens.bgSurface,
          backgroundImage: 'none', // CRITICAL — removes
                                   // MUI elevation gradient
          border: `1px solid ${tokens.border}`,
          borderRadius: '16px',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
        },
        elevation2: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
         },
      },
   },

    // ── INPUTS ─────────────────────────────────────────
    // Input text: DM Sans
    // Input label: DM Mono (uppercase tracking label)
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily: FONT_BODY, // DM Sans — typed text
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '10px',
          fontSize: '0.9375rem',
          color: tokens.textPrimary,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.borderStrong,
          },
          '&.Mui-focused': {
            background: 'rgba(0,196,167,0.05)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.teal,
              borderWidth: '1px',
              boxShadow: '0 0 0 3px rgba(0,196,167,0.12)',
            },
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.red,
          },
        },
        input: {
          '&::placeholder': {
            color: tokens.textDisabled,
            opacity: 1,
          },
         },
      },
   },

    MuiInputLabel: {
      styleOverrides: {
         root: {
          fontFamily: FONT_MONO, // DM Mono — field labels
          fontSize: '0.625rem',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: tokens.textMuted,
          '&.Mui-focused': { color: tokens.teal },
         },
      },
   },

    MuiSelect: {
      styleOverrides: {
        // Select text: DM Sans (inherited from
        // MuiOutlinedInput)
        icon: { color: tokens.textMuted },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },

    // ── NAVIGATION ─────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: tokens.bgSurface,
          border: 'none',
          borderRight: `1px solid ${tokens.border}`,
         },
      },
   },

    MuiListItemButton: {
      styleOverrides: {
         root: {
          borderRadius: '10px',
          margin: '1px 8px',
          padding: '8px 10px',
          color: tokens.textMuted,
          transition: 'all 0.15s ease',
          '&:hover': {
            background: 'rgba(255,255,255,0.05)',
            color: 'rgba(255,255,255,0.75)',
          },
          '&.Mui-selected': {
            background: tokens.tealDim,
            color: tokens.teal,
            '&:hover': {
              background: 'rgba(0,196,167,0.15)',
            },
         },
      },
   },
    },

    MuiListItemIcon: {
      styleOverrides: {
         root: {
          minWidth: '32px',
          color: 'inherit',
         },
      },
   },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: FONT_BODY, // DM Sans — nav item text
          fontSize: '0.8125rem',
          fontWeight: 500,
         },
      },
   },

    // ── TABS ───────────────────────────────────────────
    // Tab labels: DM Sans
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokens.border}`,
          minHeight: '48px',
        },
        indicator: {
          backgroundColor: tokens.teal,
          height: '2px',
         },
      },
   },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT_BODY, // DM Sans — tab labels
          fontWeight: 500,
          fontSize: '0.875rem',
          textTransform: 'none',
          letterSpacing: '0',
          color: tokens.textMuted,
          minHeight: '48px',
          padding: '12px 20px',
          transition: 'color 0.2s',
          '&:hover': {
            color: 'rgba(255,255,255,0.75)'
          },
          '&.Mui-selected': {
            color: tokens.teal,
            fontWeight: 600,
            },
         },
      },
   },

    // ── CHIPS / BADGES ─────────────────────────────────
    // ALL chip text: DM Mono uppercase
    // "Analytics", "Private", "Public", "Pending",
    // "Accepted", "Expired", "Conference", etc.
    MuiChip: {
      styleOverrides: {
         root: {
          fontFamily: FONT_MONO, // DM Mono — all chip text
          fontSize: '0.625rem',
          fontWeight: 500,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          borderRadius: '999px',
          height: '24px',
          // color="error" → red chip
          '&.MuiChip-colorError': {
            background: tokens.redDim,
            border: `1px solid rgba(255,77,106,0.25)`,
            color: tokens.red,
            // Use for: "Expired", "Denied"
          },
          // color="success" → green chip
          '&.MuiChip-colorSuccess': {
            background: tokens.greenDim,
            border: `1px solid rgba(34,197,94,0.25)`,
            color: tokens.green,
            // Use for: "Accepted", "Active", "Approved"
          },
          // color="info" → blue chip
          '&.MuiChip-colorInfo': {
            background: tokens.blueDim,
            border: `1px solid rgba(91,138,245,0.25)`,
            color: tokens.blue,
            // Use for: "Link", "Invited"
          },
        },
        filled: {
          background: 'rgba(255,255,255,0.07)',
          border: `1px solid ${tokens.border}`,
          color: tokens.textMuted,
        },
        // color="primary" → teal chip
        filledPrimary: {
          background: tokens.tealDim,
          border: `1px solid rgba(0,196,167,0.25)`,
          color: tokens.teal,
          // Use for: "Analytics", "Public", active states
        },
        // color="secondary" → amber chip
        filledSecondary: {
          background: tokens.amberDim,
          border: `1px solid rgba(240,165,0,0.25)`,
          color: tokens.amber,
          // Use for: "Pending", "Waiting", access requests
        },
      },
   },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontFamily: FONT_MONO, // DM Mono — count badges
          fontSize: '0.625rem',
          fontWeight: 700,
          minWidth: '18px',
          height: '18px',
          padding: '0 5px',
          background: tokens.red,
          color: '#fff',
         },
      },
   },

    // ── DIALOGS / MODALS ───────────────────────────────
    // Title: Cormorant Garamond
    // Content body: DM Sans
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: tokens.bgSurface,
          backgroundImage: 'none',
          border: `1px solid ${tokens.border}`,
          borderRadius: '20px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
         },
      },
   },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY, // Cormorant Garamond
          fontSize: '1.5rem',
          fontWeight: 300,
          letterSpacing: '-0.02em',
          padding: '24px 28px 12px',
         },
      },
   },

    MuiDialogContent: {
      styleOverrides: {
         root: {
          fontFamily: FONT_BODY, // DM Sans
          padding: '12px 28px 24px',
          borderTop: `1px solid ${tokens.border}`,
         },
      },
   },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: `1px solid ${tokens.border}`,
          gap: '8px',
         },
      },
   },

    // ── MENUS / DROPDOWNS ──────────────────────────────
    // Menu item text: DM Sans
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: tokens.bgOverlay,
          backgroundImage: 'none',
          border: `1px solid ${tokens.borderStrong}`,
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          minWidth: '180px',
         },
      },
   },

    MuiMenuItem: {
      styleOverrides: {
         root: {
          fontFamily: FONT_BODY, // DM Sans — menu items
          fontSize: '0.875rem',
          color: tokens.textSecondary,
          borderRadius: '8px',
          margin: '2px 6px',
          padding: '8px 12px',
          transition: 'all 0.15s',
          '&:hover': {
            background: 'rgba(255,255,255,0.06)',
            color: tokens.textPrimary,
          },
          '&.Mui-selected': {
            background: tokens.tealDim,
            color: tokens.teal,
            },
         },
      },
   },

    // ── TOOLTIPS ───────────────────────────────────────
    // Tooltip text: DM Sans
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: FONT_BODY, // DM Sans
          background: tokens.bgOverlay,
          border: `1px solid ${tokens.borderStrong}`,
          borderRadius: '8px',
          fontSize: '0.75rem',
          color: tokens.textPrimary,
          padding: '7px 12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        },
        arrow: { color: tokens.bgOverlay },
      },
    },

    // ── TABLES ─────────────────────────────────────────
    // Table headers: DM Mono uppercase
    // Table body cells: DM Sans
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontFamily: FONT_MONO, // DM Mono — headers
            fontSize: '0.625rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: tokens.teal,
            borderBottom: `1px solid rgba(0,196,167,0.25)`,
            background: tokens.bgSurface,
            fontWeight: 500,
            padding: '10px 16px',
            },
         },
      },
   },

    MuiTableBody: {
      styleOverrides: {
         root: {
          '& .MuiTableRow-root': {
            transition: 'background 0.15s',
            '&:hover': {
              background: 'rgba(255,255,255,0.02)',
            },
          },
          '& .MuiTableCell-root': {
            fontFamily: FONT_BODY, // DM Sans — cell text
            fontSize: '0.8125rem',
            color: tokens.textMuted,
            borderBottom: `1px solid ${tokens.border}`,
            padding: '12px 16px',
            },
         },
      },
   },

    // ── DIVIDER ────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.border },
      },
    },

    // ── AVATAR ─────────────────────────────────────────
    // Avatar initials: DM Sans Bold
    MuiAvatar: {
      styleOverrides: {
         root: {
          fontFamily: FONT_BODY, // DM Sans — initials
          fontWeight: 700,
          background: tokens.teal,
          color: '#000000',
        },
        colorDefault: {
          background: tokens.teal,
          color: '#000000',
         },
      },
   },

    // ── ALERTS ─────────────────────────────────────────
    // Alert text: DM Sans
    MuiAlert: {
      styleOverrides: {
         root: {
          fontFamily: FONT_BODY, // DM Sans
          borderRadius: '12px',
          fontSize: '0.875rem',
          border: '1px solid',
        },
        filledSuccess: {
          background: tokens.greenDim,
          borderColor: 'rgba(34,197,94,0.25)',
          color: tokens.green,
        },
        filledError: {
          background: tokens.redDim,
          borderColor: 'rgba(255,77,106,0.25)',
          color: tokens.red,
        },
        filledWarning: {
          background: tokens.amberDim,
          borderColor: 'rgba(240,165,0,0.25)',
          color: tokens.amber,
        },
        filledInfo: {
          background: tokens.blueDim,
          borderColor: 'rgba(91,138,245,0.25)',
          color: tokens.blue,
         },
      },
   },

    // ── LINEAR PROGRESS ────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
         root: {
          background: 'rgba(255,255,255,0.06)',
          borderRadius: '999px',
          height: '4px',
        },
        bar: {
          background: `linear-gradient(
            to right, ${tokens.teal}, #00E8C8
          )`,
          borderRadius: '999px',
         },
      },
   },

    // ── CIRCULAR PROGRESS ──────────────────────────────
    MuiCircularProgress: {
      defaultProps: { color: 'primary' },
      styleOverrides: {
        colorPrimary: { color: tokens.teal },
      },
    },

    // ── SWITCH ─────────────────────────────────────────
    MuiSwitch: {
      styleOverrides: {
         root: {
          '& .MuiSwitch-track': {
            background: 'rgba(255,255,255,0.15)',
            opacity: 1,
          },
          '& .Mui-checked .MuiSwitch-thumb': {
            color: tokens.teal,
          },
          '& .Mui-checked + .MuiSwitch-track': {
            background: tokens.teal,
            opacity: 0.5,
            },
         },
      },
   },

    // ── SKELETON ───────────────────────────────────────
    MuiSkeleton: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.07)',
          '&::after': {
            background: `linear-gradient(
              90deg,
              transparent,
              rgba(255,255,255,0.04),
              transparent
            )`,
            },
         },
      },
   },

    // ── APP BAR (top bar) ──────────────────────────────
    // AppBar text: DM Sans (inherited)
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(8,10,15,0.92)',
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${tokens.border}`,
          boxShadow: 'none',
          backgroundImage: 'none',
         },
      },
   },

    // ── TOOLBAR ────────────────────────────────────────
    MuiToolbar: {
      styleOverrides: {
        root: {
          fontFamily: FONT_BODY, // DM Sans
        },
      },
    },

    // ── TYPOGRAPHY COMPONENT ───────────────────────────
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // Cormorant Garamond variants
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          // DM Sans variants
          h5: 'h5',
          h6: 'h6',
          // DM Mono variants
          overline: 'span',
          caption: 'span',
          subtitle2: 'span',
        },
      },
    },
  },
});

// Device detection utils (kept for compatibility)
const deviceDetection = {
   isMobile: `@media (max-width:${theme.breakpoints.values.sm - 1}px)`,
   isTablet: `@media (min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md - 1}px)`,
   isDesktop: `@media (min-width:${theme.breakpoints.values.md}px)`,
   isLargeDesktop: `@media (min-width:${theme.breakpoints.values.lg}px)`,
};

// Export as a utility function to ensure theme type safety
export const getResponsiveTheme = (): Theme => theme;

// Export device detection utils
export { deviceDetection };

export default theme;
